// Centralized service for tracking and updating user IP country
import { supabase } from '../api/supabase';

export interface IpCountryResult {
  success: boolean;
  country?: string;
  ipCountry?: string;
  profileCountry?: string;
  mismatch?: boolean;
  trustScore?: number;
  error?: string;
}

export class IpCountryService {
  private static instance: IpCountryService;
  private lastUpdateTime: number = 0;
  private updateThrottleMs: number = 5 * 60 * 1000; // 5 minutes
  private lastDetectedCountry: string | null = null;

  private constructor() {}

  public static getInstance(): IpCountryService {
    if (!IpCountryService.instance) {
      IpCountryService.instance = new IpCountryService();
    }
    return IpCountryService.instance;
  }

  /**
   * Detect user's country from IP address
   */
  async detectCountry(): Promise<string> {
    // Return cached country if detected recently (within 1 minute)
    if (this.lastDetectedCountry && Date.now() - this.lastUpdateTime < 60000) {
      return this.lastDetectedCountry;
    }

    try {
      // Try to get country from server-side detection first
      const response = await fetch('/api/get-country');
      const data = await response.json();

      if (data.success && data.country) {
        const country = data.country.toUpperCase();
        this.lastDetectedCountry = country;
        return country;
      }
    } catch (error) {
    // Console statement removed
  }

    // Fallback to default
    const fallbackCountry = 'US';
    this.lastDetectedCountry = fallbackCountry;
    return fallbackCountry;
  }

  /**
   * Update user's IP country in the database
   * Throttled to prevent excessive updates
   */
  async updateUserIpCountry(userId: string, forceUpdate: boolean = false): Promise<IpCountryResult> {
    try {
      // Throttle updates unless forced
      const now = Date.now();
      if (!forceUpdate && (now - this.lastUpdateTime < this.updateThrottleMs)) {
        return {
          success: true,
          country: this.lastDetectedCountry || 'US',
          error: 'Update throttled - too soon since last update'
        };
      }

      // Detect current country
      const country = await this.detectCountry();

      // Update in database using the update_user_ip_country function
      const { data, error } = await supabase.rpc('update_user_ip_country', {
        p_user_id: userId,
        p_ip_country: country
      });

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      // Update last update time
      this.lastUpdateTime = now;

      // Parse response
      const result = data as any;

      return {
        success: true,
        country,
        ipCountry: result?.ip_country,
        profileCountry: result?.profile_country,
        mismatch: result?.mismatch || false,
        trustScore: result?.trust_score
      };

    } catch (error: any) {
      return {
        success: false,
        error: error?.message || 'Failed to update IP country'
      };
    }
  }

  /**
   * Track user IP country on key events
   * This is a convenience method that handles errors gracefully
   */
  async trackUserCountry(userId: string | undefined, event: string): Promise<void> {
    if (!userId) {
      return;
    }

    try {
      const result = await this.updateUserIpCountry(userId, false);

      if (result.success) {

        // Log warnings for location mismatches
        if (result.mismatch) {
    // Console statement removed
  }
      } else {
    // Console statement removed
  }
    } catch (error) {
    // Console statement removed
  }
  }

  /**
   * Force an immediate update (bypasses throttling)
   */
  async forceUpdate(userId: string): Promise<IpCountryResult> {
    return this.updateUserIpCountry(userId, true);
  }

  /**
   * Reset throttle (useful for testing)
   */
  resetThrottle(): void {
    this.lastUpdateTime = 0;
    this.lastDetectedCountry = null;
  }
}

export default IpCountryService;
